/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo } from 'react';
import { Stack, Divider, Box } from '@mui/material';
import {
  allPaddocksHectares,
  allSeedsKg,
  hasPremixInOrder,
  roundedTo,
} from '../utils/calculateTotal';
import { SeedOrdersInfo } from '../types/SeedOrderFormType';
import SeedFormTitle from './SeedFormTitle';
import Typography from '../../../components/Typography';
import { colors } from '@farmlands/mui-theme';
import { formatPrice } from '../../../util/helpers';

const TotalRow = ({
  label,
  value,
  valuePropsSx,
  enableGstExclLabel,
}: {
  label: string;
  value: string;
  valuePropsSx?: React.CSSProperties;
  enableGstExclLabel?: boolean;
}) => {
  return (
    <>
      <Stack direction='row' justifyContent='space-between' p={2} alignItems='center'>
        <Box display='flex' gap={1} alignItems='center'>
          <Typography variant='h5' color='primary500'>
            {label}
          </Typography>
          {enableGstExclLabel && (
            <Typography sx={{ fontSize: '12px' }} color='secondary700'>
              (Gst excl.)
            </Typography>
          )}
        </Box>
        <Typography variant='body2' sx={{ ...valuePropsSx }}>
          {value}
        </Typography>
      </Stack>
      <Divider />
    </>
  );
};

export const SeedOrderSummaryTotal = ({ seedOrder }: { seedOrder: SeedOrdersInfo }) => {
  const totalCost = useMemo(() => {
    let total = 0;
    seedOrder.mixes.forEach((mix) => {
      total += mix.subtotalAmountExcGst ?? 0;
    });
    return total;
  }, [seedOrder]);

  const totalGst = useMemo(() => {
    let total = 0;
    seedOrder.mixes.forEach((mix) => {
      total += mix.gstAmount ?? 0;
    });
    return total;
  }, [seedOrder]);

  const sowingRate = useMemo(() => {
    let total = 0;
    seedOrder.mixes.forEach((mix) => {
      const weight = allSeedsKg(mix.products);
      const paddockHectares = allPaddocksHectares(mix.paddocks);
      total += weight / paddockHectares;
    });
    return roundedTo(total).toString();
  }, [seedOrder]);

  const treatment = useMemo(() => {
    let anyTreatment = false;
    seedOrder.mixes.forEach((mix) => {
      mix.products.forEach((product) => {
        if (product.treatment) anyTreatment = true;
      });
    });
    return anyTreatment;
  }, [seedOrder]);

  return (
    <Stack bgcolor={colors.shade0} sx={{ breakBefore: 'page' }}>
      <SeedFormTitle title='Summary'>
        <Typography variant='body2'>
          Delivery costs will be determined and confirmed during order processing.
        </Typography>
      </SeedFormTitle>
      <TotalRow
        label='Total cost'
        value={formatPrice(totalCost)}
        valuePropsSx={{ fontWeight: 'bold' }}
        enableGstExclLabel
      />
      {hasPremixInOrder(seedOrder.mixes) ? null : (
        <TotalRow label='Total sowing rate' value={`${sowingRate} kg/Ha`} />
      )}
      {treatment ? <TotalRow label='Treatment' value='TBC' /> : null}
      <TotalRow label='Freight' value='TBC' />
      <TotalRow label='GST' value={formatPrice(totalGst)} />
    </Stack>
  );
};
